import { Link } from 'react-router-dom';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: {
    name: string;
    avatar: string;
  };
  date: string;
  readTime: string;
  coverImage: string;
  featured?: boolean;
}

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

export default function BlogCard({ post, featured = false }: BlogCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (featured) {
    return (
      <Link 
        to={`/blog/${post.slug}`}
        className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      >
        <div className="relative">
          <div className="w-full h-80 overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1.5 bg-[#0066CC] text-white text-xs font-semibold rounded-full whitespace-nowrap">
              {post.category}
            </span>
          </div>
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
              Featured
            </span>
          </div>
        </div>
        
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#0066CC] transition-colors line-clamp-2">
            {post.title}
          </h2>
          
          <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
            {post.excerpt}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 overflow-hidden">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-full h-full object-cover object-top rounded-full"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {post.author.name}
                </p>
                <p className="text-xs text-gray-500 whitespace-nowrap">
                  {formatDate(post.date)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <i className="ri-time-line"></i>
                <span className="whitespace-nowrap">{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/blog/${post.slug}`}
      className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
    >
      <div className="relative">
        <div className="w-full h-48 overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-[#0066CC] text-white text-xs font-semibold rounded-full whitespace-nowrap">
            {post.category}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#0066CC] transition-colors line-clamp-2">
          {post.title}
        </h3>
        
        <p className="text-gray-600 mb-4 text-sm line-clamp-2 leading-relaxed">
          {post.excerpt}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 overflow-hidden">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-full h-full object-cover object-top rounded-full"
              />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                {post.author.name}
              </p>
              <p className="text-xs text-gray-500 whitespace-nowrap">
                {formatDate(post.date)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-1 text-xs text-gray-500">
            <i className="ri-time-line"></i>
            <span className="whitespace-nowrap">{post.readTime}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}